import React from "react";
import { useTranslation } from "react-i18next";
import { Calculator } from "lucide-react";

/**
 * InvoiceTaxBadge — petit badge tax_label / tax_rate sur la page Analysis
 * d'une facture (iter62).
 *
 * - Si enrichment.tax_label est renseigné → affiche "Taxe : {label} ({rate}%)".
 * - Sinon si tax_rate seul → affiche "Taxe : {rate}%".
 * - Sinon (ni label ni rate) → ne rend rien (null) pour éviter le bruit visuel.
 *
 * Le composant est volontairement minimaliste — la décomposition par sous-régime
 * (TPS+TVQ ligne par ligne) reste dans le formulaire de saisie ManualInvoice.
 * Ici, on affiche juste le total payé pour le scan rapide.
 */
export default function InvoiceTaxBadge({ enrichment }) {
  const { t } = useTranslation("invoices");
  if (!enrichment) return null;
  const label = enrichment.tax_label;
  const rate = enrichment.tax_rate;
  const amount = enrichment.tax_amount;
  if (!label && (rate == null || rate === "")) return null;

  const formattedRate = rate != null
    ? Number(rate).toLocaleString(undefined, { maximumFractionDigits: 3 })
    : null;

  return (
    <div
      className="rounded-xl border border-zinc-200 bg-white p-4 flex items-start gap-3"
      data-testid="invoice-tax-badge"
    >
      <div className="w-9 h-9 rounded-lg bg-qenva-cat-financing/10 grid place-items-center shrink-0">
        <Calculator size={14} strokeWidth={1.75} className="text-qenva-cat-financing" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-1">
          {t("analysis_panel.tax_kicker")}
        </p>
        <p className="text-[14px] font-semibold text-zinc-900" data-testid="invoice-tax-label-text">
          {label
            ? (formattedRate ? `${label} (${formattedRate} %)` : label)
            : `${formattedRate} %`}
        </p>
        {amount != null && (
          <p className="text-[12px] text-zinc-500 mt-0.5 tabular-nums">
            {t("analysis_panel.tax_amount_line", {
              amount: Number(amount).toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              }),
              currency: enrichment.currency || "CAD",
            })}
          </p>
        )}
      </div>
    </div>
  );
}
